import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Table, Input, Form, Drawer } from "antd";
import deleteIcon from "../../../../assets/svg/delete_agency.svg";
import "./index.scss";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewQuoteAction,
  deleteQuoteRowAction,
  getAgencyQuotesFormDataAction,
  patchAgencyQuotesAction,
  setAgencyQuotesRowData,
} from "../../../../store/actions/raiseAdRequest";
import { useParams } from "react-router-dom";
import { RaiseAdRequestState } from "../../../../store/types/raiseAdRequest";
import DeleteResourceModal from "../../../AgencyFileDrop/components/DeleteResourceModal";
import { debounce } from "lodash";

import emptyQuestRequestIcon from "../../../../assets/svg/emptyAdScreenIcon.svg";
import {
  canPerformAction,
  StepKeysName,
  StepNames,
} from "../../../RaiseNoticeAdRequest/utils";
import cancelIcon from "../../../../assets/svg/circledCancelIcon.svg";
import { NoticeAdComments } from "../NoticeAdComments";
import { openToast } from "../../../../Toasts";

interface RowData {
  key: string;
  publication?: string;
  edition?: string;
  issue?: string;
  language?: string;
  width?: string;
  height?: string;
  insertions?: string;
  netCost?: string;
  creativeFormat?: string;
  isSummary?: boolean;
}

const permittedRoles = ["admin", "designer-own-access", "product-admin"];
export const QuoteDetailsForm: FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    quoteDetailsFormData,
    quoationApprovalFormData,
    userRole,
    lastStepName,
  } = useSelector(
    (state: { raiseAdRequest: RaiseAdRequestState }) => state.raiseAdRequest
  );
  const isStepAccessible = canPerformAction(
    userRole,
    permittedRoles,
    StepNames[1],
    lastStepName,
    quoationApprovalFormData.trashed_at
  );


  const { requestId, selectedProductId } = useParams<{
    requestId: string;
    selectedProductId: string;
  }>();
  const [rows, setRows] = useState([
    ...quoteDetailsFormData.data,
    ...quoteDetailsFormData.quote_pricing,
  ]);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(
    false
  );
  const [deleteRowId, setDeleteRowId] = useState<number | null>(null);
  const [isFormValuesChanged, setIsFormValuesChanged] = useState<boolean>(
    false
  );
  const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] = useState(false);

  useEffect(() => {
    if (quoteDetailsFormData?.data || quoteDetailsFormData?.quote_pricing) {
      setRows([
        ...(quoteDetailsFormData.data || []),
        ...(quoteDetailsFormData.quote_pricing || []),
      ]);
    }
  }, [quoteDetailsFormData]);

  useEffect(() => {
    dispatch(getAgencyQuotesFormDataAction(requestId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isStepAccessible && !!userRole) {
      openToast("info", "You only have view access to this information.");
    }
  }, [isStepAccessible, userRole]);

  useEffect(() => {
    if (rows.length) {
      const formatted = rows.reduce((acc, row) => {
        const identifier = row.id ?? row.key; // <-- handle both id and key
        acc[identifier] = {
          publication: row.publication,
          edition: row.edition,
          issue: row.issue,
          language: row.language,
          width: row.width,
          height: row.height,
          insertions: row.insertions,
          net_cost: row.net_cost,
          creative_format: row.creative_format,
        };
        return acc;
      }, {});
      form.setFieldsValue(formatted);
    }
  }, [rows, form]);

  const handleChange = useMemo(
    () =>
      debounce(
        (identifier: string | number, field: string, value: string) => {
          dispatch(setAgencyQuotesRowData({ identifier, field, value }));
        },
        300 // wait 300ms after user stops typing
      ),
    [dispatch]
  );

  const handleRowAddition = useCallback(() => {
    if (!isStepAccessible) return;
    dispatch(addNewQuoteAction(requestId));
  }, [dispatch, isStepAccessible, requestId]);

  const handleDeleteModalVisible = useCallback(
    (id: number) => {
      if (!isStepAccessible) return;
      setIsDeleteModalVisible(true);
      setDeleteRowId(id);
    },
    [isStepAccessible]
  );

  const handleDeleteRow = useCallback(() => {
    dispatch(deleteQuoteRowAction(deleteRowId));
    setIsDeleteModalVisible(false);
    setDeleteRowId(null);
  }, [deleteRowId, dispatch]);

  const handleChangeImmediate = useCallback(
    (identifier: string | number, field: string, value: string) => {
      dispatch(setAgencyQuotesRowData({ identifier, field, value }));
    },
    [dispatch]
  );

  const columns = [
    {
      title: "Publication",
      dataIndex: "publication",
      render: (_: any, row) =>
        !row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "publication"]}
            style={{ margin: 0 }}
            rules={[
              { required: true, message: `Required Field` },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message: "Maximum 100 characters allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              readOnly={!isStepAccessible}
              value={row.publication}
              style={{ width: "15rem", margin: "0.5rem 0" }}
              onChange={(e) =>
                handleChange(row.id ?? row.key, "publication", e.target.value)
              }
            />
          </Form.Item>
        ) : null,
    },
    {
      title: "Edition",
      dataIndex: "edition",
      render: (_: any, row) =>
        !row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "edition"]}
            style={{ margin: 0 }}
            rules={[
              { required: true, message: `Required Field` },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message: "Maximum 100 characters allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              readOnly={!isStepAccessible}
              value={row.edition}
              style={{ width: "10rem" }}
              onChange={(e) =>
                handleChange(row.id ?? row.key, "edition", e.target.value)
              }
            />
          </Form.Item>
        ) : null,
    },
    {
      title: "Issue",
      dataIndex: "issue",
      render: (_: any, row) =>
        !row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "issue"]}
            style={{ margin: 0 }}
            rules={[
              { required: true, message: `Required Field` },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message: "Maximum 100 characters allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              readOnly={!isStepAccessible}
              value={row.issue}
              style={{ width: "15rem", margin: "0.5rem 0" }}
              onChange={(e) =>
                handleChange(row.id ?? row.key, "issue", e.target.value)
              }
            />
          </Form.Item>
        ) : null,
    },
    {
      title: "Language",
      dataIndex: "language",
      render: (_: any, row) =>
        !row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "language"]}
            style={{ margin: 0 }}
            rules={[
              { required: true, message: `Required Field` },
              {
                pattern: /^[a-zA-Z ]+$/, // ✅ only letters + spaces allowed
                message: "Only alphabets are allowed",
              },
              {
                max: 100,
                message: "Maximum 100 characters allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              readOnly={!isStepAccessible}
              value={row.language}
              onChange={(e) =>
                handleChange(row.id ?? row.key, "language", e.target.value)
              }
              onKeyPress={(e) => {
                if (!/[a-zA-Z ]/.test(e.key)) {
                  e.preventDefault(); // block numbers & special chars
                }
              }}
            />
          </Form.Item>
        ) : null,
    },
    {
      title: "Width(cm)",
      dataIndex: "width",
      render: (_: any, row) =>
        !row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "width"]}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                pattern: /^[0-9]+$/,
                message: (
                  <>
                    <CloseCircleOutlined
                      style={{ color: "red", marginRight: 4 }}
                    />
                    Enter numerical value only
                  </>
                ),
              },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              value={row.width}
              readOnly={!isStepAccessible}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  // allow only digits
                  handleChange(row.id ?? row.key, "width", val);
                }
              }}
            />
          </Form.Item>
        ) : null,
    },
    {
      title: "Height(cm)",
      dataIndex: "height",
      render: (_: any, row) =>
        !row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "height"]}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                pattern: /^[0-9]+$/,
                message: (
                  <>
                    <CloseCircleOutlined
                      style={{ color: "red", marginRight: 4 }}
                    />
                    Enter numerical value only
                  </>
                ),
              },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              readOnly={!isStepAccessible}
              value={row.height}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  // allow only digits
                  handleChange(row.id ?? row.key, "height", val);
                }
              }}
            />
          </Form.Item>
        ) : null,
    },
    {
      title: "Insertions",
      dataIndex: "insertions",
      render: (_: any, row) =>
        row.isSummary ? (
          row.insertions
        ) : (
          <Form.Item
            name={[row.id || row.key, "insertions"]}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                pattern: /^[0-9]+$/,
                message: (
                  <>
                    <CloseCircleOutlined
                      style={{ color: "red", marginRight: 4 }}
                    />
                    Enter numerical value only
                  </>
                ),
              },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              value={row.insertions}
              readOnly={!isStepAccessible}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  // allow only digits
                  handleChange(row.id ?? row.key, "insertions", val);
                }
              }}
            />
          </Form.Item>
        ),
    },
    {
      title: "Net Cost",
      dataIndex: "net_cost",
      render: (_: any, row) =>
        row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "net_cost"]}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                pattern: /^\d+(\.\d+)?$/,
                message: (
                  <>
                    <CloseCircleOutlined
                      style={{ color: "red", marginRight: 4 }}
                    />
                    Enter numerical value only
                  </>
                ),
              },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
            ]}
          >
            <Input
              autoComplete="off"
              readOnly={!isStepAccessible}
              value={row.net_cost ?? 0}
              style={{ width: "15rem", margin: "0.5rem 0" }}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*\.?\d*$/.test(val)) {
                  handleChangeImmediate(row.id ?? row.key, "net_cost", val);
                }
              }}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name={[row.id || row.key, "net_cost"]}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                pattern: /^\d+(\.\d{1,2})?$/, // ✅ allow up to 2 decimal places
                message: (
                  <>
                    <CloseCircleOutlined
                      style={{ color: "red", marginRight: 4 }}
                    />
                    Enter numerical value (up to 2 decimals) only
                  </>
                ),
              },
            ]}
          >
            <Input
              autoComplete="off"
              readOnly={!isStepAccessible}
              value={row.net_cost ?? ""}
              style={{ width: "15rem", margin: "0.5rem 0" }}
              onChange={(e) => {
                const val = e.target.value;
                // ✅ allow only digits + 1 optional decimal point with up to 2 decimals
                if (/^\d*\.?\d{0,2}$/.test(val)) {
                  handleChange(row.id ?? row.key, "net_cost", val);
                }
              }}
            />
          </Form.Item>
        ),
    },
    {
      title: "Creative Format",
      dataIndex: "creative_format",
      render: (_: any, row) =>
        !row.isSummary ? (
          <Form.Item
            name={[row.id || row.key, "creative_format"]}
            style={{ margin: 0 }}
            rules={[
              { required: true, message: `Required Field` },
              {
                pattern: /^[a-zA-Z0-9 ]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message: "Maximum 100 characters allowed",
              },
            ]}
          >
            <div style={{ display: "flex", gap: "1rem" }}>
              <Input
                autoComplete="off"
                readOnly={!isStepAccessible}
                value={row.creative_format}
                onChange={(e) =>
                  handleChangeImmediate(
                    row.id ?? row.key,
                    "creative_format",
                    e.target.value
                  )
                }
                style={{ width: "15rem", margin: "0.5rem 0" }}
              />
              <img
                src={deleteIcon}
                alt="Delete Icon"
                onClick={() => handleDeleteModalVisible(row.id)}
                style={{ cursor: "pointer" }}
              />
            </div>
          </Form.Item>
        ) : null,
    },
  ];

  const handleSubmit = useCallback(
    (values) => {
      if (!!isFormValuesChanged) {
        dispatch(
          patchAgencyQuotesAction({
            status: "Pending",
            stage: "Marketing Approvals",
            nextStepKey: StepKeysName.MarketingApprovals,
            workspaceId: selectedProductId,
            id: requestId,
          })
        );
      }
    },
    [dispatch, isFormValuesChanged, requestId, selectedProductId]
  );

  return (
    <div className="layout-wrapper">
      {/* Scrollable Form */}

      {quoteDetailsFormData.data.length ? (
        <div className="layout-wrapper__body">
          <Form
            form={form}
            onFinish={handleSubmit}
            onValuesChange={(changedValues, allValues) => {
              if (!!isStepAccessible) {
                setIsFormValuesChanged(true);
              }
            }}
            className="custom-form quote-details-form"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "2rem",
              }}
            >
              <div
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 600,
                  color: "#394455",
                }}
              >
                Quotation Details Received From Agency
              </div>

              <div
                style={{
                  fontSize: "1.4rem",
                  fontWeight: 500,
                  color: "#6810B6",
                  cursor: "pointer",
                  marginTop: "1rem",
                }}
                onClick={() => setIsCommentsDrawerOpen(true)}
              >
                + Add Comments
              </div>
            </div>

            <Table
              className="table-custom request-table"
              pagination={false}
              dataSource={rows}
              columns={columns}
              rowKey={(record) => record.id ?? record.name}
              rowClassName={(record) => (record.isSummary ? "summary-row" : "")}
              style={{ margin: "0", marginTop: "2rem" }}
            />
            <div>
              <div
                style={{
                  fontSize: "1.4rem",
                  fontWeight: 500,
                  color: "#6810B6",
                  cursor: "pointer",
                  marginTop: "1rem",
                  width: "fit-content",
                }}
                onClick={handleRowAddition}
              >
                + Add row
              </div>
            </div>
          </Form>
        </div>
      ) : (
        <div className="default-dashboard">
          {/* Empty state */}
          <div className="default-dashboard__empty-state">
            <img src={emptyQuestRequestIcon} alt="Empty Screen" />
            <div className="default-dashboard__empty-text">
              You don’t have any raised requests
            </div>
            <button className="btn btn--purple" onClick={handleRowAddition}>
              Add Row
            </button>
          </div>
        </div>
      )}
      {!!isStepAccessible ? (
        <div className="layout-wrapper__footer">
          <button
            className="btn btn--purple"
            disabled={!quoteDetailsFormData.data.length || !isFormValuesChanged}
            onClick={() => form.submit()}
          >
            Submit for approval
          </button>
        </div>
      ) : (
        <></>
      )}

      <DeleteResourceModal
        isModalOpen={isDeleteModalVisible}
        setModalOpen={setIsDeleteModalVisible}
        modalTitle="Delete Quote?"
        modalDesc="Are you sure you want to delete this quote, all the information under this quote will be permanently removed."
        handleDeleteClick={handleDeleteRow}
        img={cancelIcon}
      />

      <NoticeAdComments
        setIsCommentsDrawerOpen={setIsCommentsDrawerOpen}
        isCommentsDrawerOpen={isCommentsDrawerOpen}
      />
    </div>
  );
};
